// frontend/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { 
  BuildingOfficeIcon, 
  UserIcon, 
  LockClosedIcon,
  CheckCircleIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  CubeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [contactForm, setContactForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Add metadata to head
  useEffect(() => {
    document.title = 'Brominon - Smart Invoicing & POS System for Kenyan Businesses';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Brominon is a powerful POS and invoicing system designed for Kenyan businesses. Manage sales, inventory, customers, and generate KRA-compliant invoices. Start your free trial today!';
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'POS system, invoicing software, inventory management, Kenyan businesses, retail software, restaurant POS, billing system';
    
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      document.head.appendChild(viewport);
    }
    viewport.content = 'width=device-width, initial-scale=1.0';
    
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = 'Brominon - Smart Invoicing & POS System';
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = 'Complete business management solution for Kenyan businesses. POS, invoicing, inventory, and more.';
    
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.content = 'website';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ username, password, tenantId });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsAppInquiry = () => {
    const { fullName, email, phone, message } = contactForm;
    
    // Validate required fields
    if (!fullName || !email || !phone || !message) {
      alert('Please fill in all fields before sending');
      return;
    }
    
    // Format WhatsApp message
    const whatsappMessage = `*Brominon - New Inquiry*%0A%0A*Customer Details:*%0A
• *Name:* ${fullName}%0A
• *Email:* ${email}%0A
• *Phone:* ${phone}%0A%0A
*Message:*%0A${message}%0A%0A
*Sent from:* Brominon Website%0A
*Date:* ${new Date().toLocaleString()}`;
    
    // WhatsApp business number
    const whatsappNumber = '254798331960';
    
    // Open WhatsApp
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
    
    // Reset form
    setContactForm({
      fullName: '',
      email: '',
      phone: '',
      message: ''
    });
    
    setIsSubmitting(false);
  };

  const handleGetStarted = (planName) => {
    const whatsappMessage = `*Brominon POS/Invoicing - New Lead*%0A%0A
*Plan Interested In:* ${planName}%0A
*Interested In:* Getting started with Brominon%0A
*Source:* Website POS Page%0A
*Date:* ${new Date().toLocaleString()}%0A%0A
Please contact me with more information.`;
    
    const whatsappNumber = '254798331960';
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const pricingPlans = [
    { 
      name: 'Basic (Starter)', 
      price: '1,500', 
      target: 'Retail Shops / Small Dukas',
      icon: BuildingStorefrontIcon,
      color: 'from-purple-600 to-indigo-600',
      features: ['1 User', '100 Products', 'Basic Sales', 'Email Support'],
      buttonColor: 'bg-purple-600 hover:bg-purple-700'
    },
    { 
      name: 'Standard (Growth)', 
      price: '3,500', 
      target: 'Wholesale / Hardware',
      icon: ChartBarIcon,
      color: 'from-purple-700 to-indigo-700',
      features: ['3 Users', '500 Products', 'Inventory Alerts', 'Credit Sales', 'Priority Support'],
      buttonColor: 'bg-purple-700 hover:bg-purple-800',
      popular: true
    },
    { 
      name: 'Premium (Pro)', 
      price: '7,500', 
      target: 'Supermarkets / Restaurants',
      icon: RocketLaunchIcon,
      color: 'from-purple-800 to-indigo-800',
      features: ['Unlimited Users', 'Unlimited Products', 'Multi-branch Support', 'P&L Reports', '24/7 Support'],
      buttonColor: 'bg-purple-800 hover:bg-purple-900'
    },
    { 
      name: 'Enterprise', 
      price: 'Custom', 
      target: 'Large Corporate / Chains',
      icon: ShieldCheckIcon,
      color: 'from-purple-900 to-indigo-900',
      features: ['Dedicated Support', 'API Access', 'Custom Features', 'SLA Agreement', 'White Label'],
      buttonColor: 'bg-purple-900 hover:bg-purple-950',
      isCustom: true
    },
  ];

  const features = [
    { icon: CurrencyDollarIcon, title: 'POS & Invoicing', description: 'Fast point of sale and professional KRA-compliant invoices' },
    { icon: CubeIcon, title: 'Inventory Management', description: 'Real-time stock tracking and low stock alerts' },
    { icon: ChartBarIcon, title: 'Business Reports', description: 'Sales, tax, profit & loss, and aging reports' },
    { icon: CheckCircleIcon, title: 'Customer Management', description: 'Track balances, send SMS, manage relationships' },
  ];

  const stats = [
    { value: '500+', label: 'Businesses', icon: BuildingOfficeIcon },
    { value: '50K+', label: 'Transactions/Month', icon: CurrencyDollarIcon },
    { value: '99.9%', label: 'Uptime', icon: CheckCircleIcon },
    { value: '24/7', label: 'Support', icon: ClockIcon },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            {/* Left Column - Hero Content */}
            <div className="mb-12 lg:mb-0">
              <div className="flex items-center mb-6">
                <BuildingOfficeIcon className="h-10 w-10 text-purple-700" />
                <h1 className="ml-3 text-3xl font-bold text-gray-900">Brominon</h1>
              </div>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
                Smart Invoicing & <br />
                <span className="text-purple-700">POS System</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Complete business management solution for Kenyan businesses. 
                Sell faster, track inventory, and grow your business.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button
                  onClick={() => setShowLogin(true)}
                  className="px-6 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors"
                >
                  Start Free Trial
                </button>
                <a
                  href="#contact"
                  className="px-6 py-3 bg-white text-gray-700 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors text-center"
                >
                  Contact Sales
                </a>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <stat.icon className="h-6 w-6 text-purple-700 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Column - Login Form */}
            {!showLogin ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                <div className="text-center mb-6">
                  <BuildingOfficeIcon className="h-12 w-12 text-purple-700 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-gray-900">Ready to Get Started?</h3>
                  <p className="text-gray-600 mt-2">Sign in to your account or create a new one</p>
                </div>
                <div className="space-y-4">
                  <button
                    onClick={() => setShowLogin(true)}
                    className="w-full px-4 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => window.location.href = '/register'}
                    className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Create New Account
                  </button>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-xs text-gray-500">
                    By signing in, you agree to our Terms of Service and Privacy Policy
                  </p>
                </div>
              </div>
            ) : (
              // Login Form
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                <div className="text-center mb-6">
                  <button
                    onClick={() => setShowLogin(false)}
                    className="text-sm text-purple-700 hover:text-purple-800 float-right"
                  >
                    ← Back
                  </button>
                  <BuildingOfficeIcon className="h-12 w-12 text-purple-700 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-gray-900">Welcome Back</h3>
                  <p className="text-gray-600 mt-2">Sign in to your account</p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant Code (Optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value.toUpperCase())}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="e.g., TECH001"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leave blank if you have only one business</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Enter your username"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Enter your password"
                      />
                    </div>
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 px-4 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                  
                  <div className="text-center">
                    <a href="/forgot-password" className="text-sm text-purple-700 hover:text-purple-800">
                      Forgot your password?
                    </a>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything You Need to Run Your Business</h2>
            <p className="text-xl text-gray-600">Powerful features designed for Kenyan businesses</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="text-center p-6">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-purple-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Pricing Section - Updated with new tiers */}
      <div className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">Choose the plan that fits your business</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan, idx) => (
              <div key={idx} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-transform hover:scale-105 ${plan.popular ? 'ring-2 ring-purple-500' : ''}`}>
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-purple-700 text-white px-3 py-1 text-sm font-semibold rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <div className={`bg-gradient-to-r ${plan.color} p-6 text-center text-white`}>
                  <plan.icon className="h-10 w-10 mx-auto mb-3" />
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm opacity-90 mt-1">{plan.target}</p>
                  <div className="mt-4">
                    {plan.isCustom ? (
                      <span className="text-3xl font-bold">Custom Quote</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">KES {plan.price}</span>
                        <span className="text-sm">/month</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleGetStarted(plan.name)}
                    className={`w-full mt-6 py-2 px-4 ${plan.buttonColor} text-white rounded-lg font-semibold transition-colors`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-8">
            * All prices are in Kenyan Shillings (KES) and exclusive of 16% VAT
          </p>
        </div>
      </div>
      
      {/* Contact Section with WhatsApp Integration */}
      <div id="contact" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-gray-600">We're here to help and answer any questions</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <EnvelopeIcon className="h-6 w-6 text-purple-700 mt-1 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Email Us</h3>
                    <p className="text-gray-600">autobelltech@proton.me</p>
                    <p className="text-gray-600"></p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <PhoneIcon className="h-6 w-6 text-purple-700 mt-1 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Call Us</h3>
                    <p className="text-gray-600">+254798331960</p>
                    <p className="text-gray-600">+254734614142</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MapPinIcon className="h-6 w-6 text-purple-700 mt-1 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Visit Us</h3>
                    <p className="text-gray-600">Nairobi, Kenya</p>
                    <p className="text-gray-600">Ngara, Corporate House</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <ClockIcon className="h-6 w-6 text-purple-700 mt-1 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Business Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p className="text-gray-600">Saturday: 9:00 AM - 1:00 PM</p>
                    <p className="text-gray-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleWhatsAppInquiry(); }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input 
                    type="text" 
                    name="fullName"
                    value={contactForm.fullName}
                    onChange={handleContactChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" 
                    placeholder="John Doe" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" 
                    placeholder="john@example.com" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" 
                    placeholder="0712345678" 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea 
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    rows={4} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" 
                    placeholder="Tell us about your business needs..." 
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full py-2 px-4 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors flex items-center justify-center gap-2"
                >
                  <PhoneIcon className="h-5 w-5" />
                  Send via WhatsApp
                </button>
              </form>
              <p className="text-xs text-gray-500 text-center mt-3">
                We'll respond to your inquiry within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-6 mb-4">
            <a href="#" className="hover:text-purple-400">About</a>
            <a href="#" className="hover:text-purple-400">Privacy Policy</a>
            <a href="#" className="hover:text-purple-400">Terms of Service</a>
            <a href="#" className="hover:text-purple-400">Contact</a>
          </div>
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Brominon Invoicing System. All rights reserved.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Made with ❤️ in Kenya for Kenyan businesses
          </p>
        </div>
      </footer>
    </div>
  );
}